import { supabase } from '@/lib/supabase/client';
import { getRecallClusters } from '@/services/spatialClustering';
import { DEFAULT_OEM } from '@/constants/oems';

/**
 * Debug utility to investigate Georgia recall data visibility issues
 */

// Georgia bounds (approximate)
const GEORGIA_BOUNDS = {
  north: 35.0, // Northern border
  south: 30.3, // Southern border
  east: -80.8, // Eastern border
  west: -85.6 // Western border
};

export async function debugGeorgiaRecalls() {
  console.log('🔍 Debugging Georgia recall data...');

  try {
    // 1. Check if there are recalls in the materialized view for Georgia
    console.log('📊 Step 1: Checking prod.cars_with_safety_recalls_mv for Georgia...');

    const { data: georgiaRecalls, error: georgiaError } = await supabase
      .from('cars_with_safety_recalls_mv')
      .select(
        `
        service_provider_name,
        seller_latitude,
        seller_longitude,
        seller_city,
        seller_region,
        vin,
        safety_recalls
      `
      )
      .gte('seller_latitude', GEORGIA_BOUNDS.south)
      .lte('seller_latitude', GEORGIA_BOUNDS.north)
      .gte('seller_longitude', GEORGIA_BOUNDS.west)
      .lte('seller_longitude', GEORGIA_BOUNDS.east)
      .not('seller_latitude', 'is', null)
      .not('seller_longitude', 'is', null)
      .limit(100);

    if (georgiaError) {
      console.error('❌ Error querying Georgia recalls:', georgiaError);
      return;
    }

    console.log(`✅ Found ${georgiaRecalls?.length || 0} vehicles in Georgia bounds`);

    if (georgiaRecalls && georgiaRecalls.length > 0) {
      console.log('📍 Sample Georgia locations:');
      georgiaRecalls.slice(0, 5).forEach((recall, index) => {
        console.log(
          `  ${index + 1}. ${recall.service_provider_name} - ${recall.seller_city}, ${recall.seller_region}`
        );
        console.log(`     Lat: ${recall.seller_latitude}, Lng: ${recall.seller_longitude}`);

        // Check if safety_recalls is valid JSON
        if (recall.safety_recalls) {
          try {
            const parsedRecalls =
              typeof recall.safety_recalls === 'string'
                ? JSON.parse(recall.safety_recalls)
                : recall.safety_recalls;
            console.log(`     Recalls: ${parsedRecalls?.length || 0} found`);
          } catch (e) {
            console.log(`     Recalls: Invalid JSON - ${e.message}`);
          }
        } else {
          console.log('     Recalls: No recall data');
        }
      });
    }

    // 2. Test the spatial clustering function
    console.log('\n🗺️ Step 2: Testing spatial clustering for Georgia...');

    const clusters = await getRecallClusters(GEORGIA_BOUNDS, 10, undefined, {
      oem: DEFAULT_OEM
    });
    console.log(`✅ Spatial clustering returned ${clusters.length} clusters for Georgia`);

    if (clusters.length > 0) {
      console.log('📍 Sample clusters:');
      clusters.slice(0, 5).forEach((cluster, index) => {
        console.log(`  ${index + 1}. ${cluster.dealerNames.join(', ')}`);
        console.log(`     Center: ${cluster.centerLat}, ${cluster.centerLon}`);
        console.log(`     Recalls: ${cluster.recallCount}, Vehicles: ${cluster.vehicleCount}`);
      });
    }

    // 3. Check for potential data filtering issues
    console.log('\n🔍 Step 3: Checking for data filtering issues...');

    // Check if user exclusions might be affecting the data
    const { data: allGeorgiaVehicles, error: allVehiclesError } = await supabase
      .from('cars_with_safety_recalls_mv')
      .select('service_provider_name, seller_city, seller_region, vin')
      .eq('seller_region', 'GA')
      .limit(10);

    if (allVehiclesError) {
      console.error('❌ Error querying all Georgia vehicles:', allVehiclesError);
    } else {
      console.log(`✅ Found ${allGeorgiaVehicles?.length || 0} vehicles with seller_region = 'GA'`);
    }

    // 4. Check materialized view freshness
    console.log('\n⏰ Step 4: Checking materialized view freshness...');

    const { data: viewInfo, error: viewError } = await supabase.rpc('get_materialized_view_info', {
      view_name: 'cars_with_safety_recalls_mv'
    });

    if (viewError) {
      console.log(
        "ℹ️ Could not get materialized view info - this is normal if the function doesn't exist"
      );
    } else {
      console.log('📊 Materialized view info:', viewInfo);
    }

    return {
      georgiaRecalls: georgiaRecalls?.length || 0,
      clusters: clusters.length,
      hasData: (georgiaRecalls?.length || 0) > 0,
      hasValidRecalls: georgiaRecalls?.some(r => r.safety_recalls) || false
    };
  } catch (error) {
    console.error('❌ Debug failed:', error);
    return null;
  }
}

// Export to window for console access
if (typeof window !== 'undefined') {
  (window as any).debugGeorgiaRecalls = debugGeorgiaRecalls;
}

export default debugGeorgiaRecalls;
